import { Alert, Button, Center, Loader, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';
import { ErrorMessage } from './ErrorMessage.js';

/**
 * The boundary between signed-out and signed-in.
 *
 * Three states, and conflating any two of them produces a wrong screen:
 *
 * - **loading** — the session is still being established. Redirecting here
 *   would bounce every page load to the login screen for a moment, including
 *   for people who are signed in.
 * - **a fault** — the API is unreachable, or answered with something that is not
 *   an answer. Say so and offer a retry. This is emphatically not "go to
 *   login": signing in would fail the same way, and the user would be left
 *   believing they had mistyped a password.
 * - **no session** — go to login.
 * - **session, no company** — signed in and there is nothing to show. This is
 *   not an error and not a reason to sign out; the API is telling the truth
 *   about a user with no memberships.
 */
export function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const t = useT();
  const { loading, hasSession, signedIn, memberships, error, refresh } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Center mih="100dvh">
        <Loader />
      </Center>
    );
  }

  if (error !== null) {
    // Not a 401: the API could not be reached, or answered with something that
    // is not an answer. Sending the user to a login form would be a lie about
    // what went wrong, and signing in would fail identically.
    return (
      <Center mih="100dvh" p="md">
        <Stack maw={480} gap="md">
          <ErrorMessage error={error} title={t('auth.sessionFailed')} />
          <Button
            onClick={() => {
              void refresh();
            }}
          >
            {t('common.tryAgain')}
          </Button>
        </Stack>
      </Center>
    );
  }

  // The session, not the company token. Someone with a valid session and no
  // memberships must not be sent to a login form they would complete
  // successfully only to arrive back here.
  if (!hasSession) {
    /*
      `state` carries where they were going, so signing in returns them there
      rather than to the dashboard.

      Search included, not just the path. It used to be the path alone, and the
      consent screen is the first page where that difference is fatal: its only
      parameter is `?request=...`, so signing in first would have dropped the
      request and landed the person on a page with nothing to approve.
    */
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (memberships.length === 0) {
    return (
      <Center mih="100dvh" p="md">
        <Stack maw={420}>
          <Alert color="yellow" title={t('auth.noCompanies')} variant="light">
            <Text size="sm">{t('auth.noCompaniesDetail')}</Text>
          </Alert>
        </Stack>
      </Center>
    );
  }

  if (!signedIn) {
    // A session and memberships, but the token exchange has not landed yet.
    // Rendering the page now would run its queries without a client.
    return (
      <Center mih="100dvh">
        <Loader />
      </Center>
    );
  }

  return children;
}
